import { FunctionComponent, memo, useState } from "react";
import { useQueryClient } from "react-query";
import toast from "react-hot-toast";
import Axios from "axios";
import { IPermissionPostRequest, IRolePermission, Route } from "@edgetier/watchtower-types";
import { getTimeToRead } from "@edgetier/utilities";
import { axios } from "utilities/axios";
import { showServerError } from "utilities/show-server-error";
import { IProps } from "./permission-toggle.types";
import { ToggleSwitch } from "@edgetier/client-components";
import "./permission-toggle.scss";

/**
 * Display a toggle switch for a permission
 * @param props.isChecked        Checked state of permission from database
 * @param props.permission
 * @param props.role
 * @param props.disabled        Disable the toggle when doesn't have permission to edit
 */
const PermissionToggle: FunctionComponent<IProps> = ({ isChecked, permission, role }) => {
    const queryClient = useQueryClient();
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const onChange = async (enabled: boolean): Promise<void> => {
        try {
            setIsLoading(true);
            if (enabled) {
                const queryUrl = Route.RolePermissions.replace(":roleId", role.roleId);
                // Assign permission with POST
                await axios.post<IPermissionPostRequest>(queryUrl, {
                    permissionId: permission.permissionId,
                });

                queryClient.setQueryData<IRolePermission>([queryUrl, role.roleId], (activePermissions) => {
                    if (typeof activePermissions !== "undefined") {
                        const [roleId, permissions] = Object.entries(activePermissions)[0];
                        const result = [...permissions, permission];
                        return { [roleId]: result };
                    }

                    return {};
                });

                const successMessage = `Permission granted`;
                toast.success(successMessage, { duration: getTimeToRead(successMessage, 1000) });
            } else {
                const queryUrl = Route.RolePermission.replace(":roleId", role.roleId).replace(
                    ":permissionId",
                    permission.permissionId.toString()
                );

                // Revoke permission with DELETE
                await axios.delete<IPermissionPostRequest>(queryUrl);

                queryClient.setQueryData<IRolePermission>(
                    [Route.RolePermissions.replace(":roleId", role.roleId), role.roleId],
                    (activePermissions) => {
                        if (typeof activePermissions !== "undefined") {
                            const [roleId, permissions] = Object.entries(activePermissions)[0];
                            const result = permissions.filter(
                                ({ permissionId }) => permissionId !== permission.permissionId
                            );
                            return { [roleId]: result };
                        }

                        return {};
                    }
                );
                const successMessage = "Permission revoked";
                toast.success(successMessage, { duration: getTimeToRead(successMessage, 1000) });
            }
        } catch (serverError) {
            if (Axios.isAxiosError(serverError)) {
                showServerError(serverError);
            } else {
                console.error(serverError);
                toast.error("Failed to update the permission");
            }
        } finally {
            setIsLoading(false);
        }
    };

    return <ToggleSwitch checked={isChecked} onChange={onChange} disabled={isLoading} />;
};

export default memo(PermissionToggle);
